import React, { useState, useCallback } from 'react';
import { AgentLayout } from '../../components/layout/AgentLayout';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Spinner } from '../../components/common/Spinner';
import { usePublicRaffles } from '../../hooks/useRaffles';
import { useWallet } from '../../hooks/useWallet';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/formatters';
import { CheckCircle } from 'lucide-react';
import { TicketReceipt } from '../../components/agent/TicketReceipt';
import { usePaystackPayment } from 'react-paystack';
import api from '../../config/api';
import toast from 'react-hot-toast';

const PAYSTACK_KEY = 'pk_test_2f7c0adb58fbcb03407090d74393e7d2f74209ac';

export const AgentSellTickets: React.FC = () => {
  const { data: rafflesData, isLoading: rafflesLoading } = usePublicRaffles({ limit: 100 });
  const { data: walletData } = useWallet();
  const { user } = useAuth();
  const [raffleId, setRaffleId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [selling, setSelling] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [pendingPayment, setPendingPayment] = useState<any>(null);

  const raffles = rafflesData?.data?.raffles || [];
  const selectedRaffle = raffles.find((r: any) => r.id === raffleId);
  const walletBalance = parseFloat(String(walletData?.data?.balance || 0));
  const currency = walletData?.data?.currency || user?.currency || 'USD';

  const totalCost = selectedRaffle ? selectedRaffle.ticketPrice * (parseInt(quantity) || 0) : 0;
  const commission = totalCost * 0.1;
  const agentPays = totalCost - commission;

  const initializePaystack = usePaystackPayment(pendingPayment ? {
    publicKey: PAYSTACK_KEY,
    email: user?.email || '',
    amount: (pendingPayment?.amountNeeded || 0) * 100,
    reference: pendingPayment?.paymentData?.reference || '',
    currency: selectedRaffle?.currency || 'NGN',
  } : { publicKey: PAYSTACK_KEY, email: '', amount: 0, reference: '' });

  const completeSellAfterPayment = useCallback(async (ref: any) => {
    try { await api.post('/webhooks/paystack/verify', { reference: ref?.reference || pendingPayment?.paymentData?.reference }); } catch {}
    try {
      const retryRes = await api.post('/agent/sell', {
        raffleId, quantity: parseInt(quantity) || 1,
        customerFirstName: firstName, customerLastName: lastName,
        customerPhone: phone, accessCode: accessCode || undefined,
      });
      setResult(retryRes.data.data);
      setPendingPayment(null);
      toast.success('Tickets sold successfully!');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Payment succeeded but sale failed. Wallet credited.');
    }
  }, [raffleId, quantity, firstName, lastName, phone, accessCode, pendingPayment]);

  const handleSell = async () => {
    if (!raffleId || !firstName || !lastName || !phone) {
      toast.error('Please fill in raffle, customer name, and phone'); return;
    }
    setSelling(true);
    try {
      const res = await api.post('/agent/sell', {
        raffleId, quantity: parseInt(quantity) || 1,
        customerFirstName: firstName, customerLastName: lastName,
        customerPhone: phone, accessCode: accessCode || undefined,
        paymentProvider: 'PAYSTACK',
      });
      const data = res.data.data;
      if (data.requiresPayment) {
        setPendingPayment(data);
        // Paystack will be triggered via useEffect when pendingPayment is set
      } else {
        setResult(data);
        toast.success('Tickets sold successfully!');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Failed to sell tickets');
    } finally { setSelling(false); }
  };

  // Trigger Paystack when pending payment is set
  React.useEffect(() => {
    if (pendingPayment) {
      initializePaystack({
        onSuccess: completeSellAfterPayment,
        onClose: () => { setPendingPayment(null); toast('Payment cancelled', { icon: 'ℹ️' }); },
      });
    }
  }, [pendingPayment]);

  const resetForm = () => {
    setResult(null); setFirstName(''); setLastName(''); setPhone(''); setQuantity('1'); setAccessCode('');
  };

  if (result) {
    return (
      <AgentLayout>
        <Card className="max-w-lg mx-auto text-center py-10">
          <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Sale Complete!</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {result.tickets?.length} ticket{result.tickets?.length > 1 ? 's' : ''} sold to {result.customer?.firstName} {result.customer?.lastName}
          </p>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-4 text-left space-y-2">
            <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">You Paid</span><span className="font-bold">{result.agentPaid?.toFixed(2)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">Commission</span><span className="font-bold text-emerald-600">+{result.commissionAmount?.toFixed(2)}</span></div>
          </div>
          <div className="mb-6">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Tickets</p>
            <div className="space-y-4 max-h-[50vh] overflow-y-auto flex flex-col items-center">
              {result.tickets?.map((t: any) => (
                <TicketReceipt
                  key={t.id}
                  ticketNumber={t.ticketNumber}
                  qrPayload={t.qrPayload}
                  raffleName={result.raffleName || selectedRaffle?.name || ''}
                  customerName={`${result.customer?.firstName} ${result.customer?.lastName}`}
                  price={parseFloat(t.purchasePrice).toFixed(2)}
                  currency={result.raffleCurrency || selectedRaffle?.currency || 'USD'}
                  purchaseDate={new Date().toLocaleDateString()}
                />
              ))}
            </div>
          </div>
          <Button onClick={resetForm}>Sell Another</Button>
        </Card>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Sell Tickets</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Sell raffle tickets on behalf of customers</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <Card>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Raffle</h3>
            {rafflesLoading ? <Spinner /> : (
              <Select label="Select Raffle" value={raffleId} onChange={(e) => setRaffleId(e.target.value)}
                options={[{ value: '', label: 'Choose a raffle...' }, ...raffles.map((r: any) => ({ value: r.id, label: `${r.name} — ${formatCurrency(r.ticketPrice, r.currency)}/ticket` }))]} />
            )}
            {selectedRaffle?.participationAccess === 'INVITE_CODE' && (
              <Input label="Access Code" value={accessCode} onChange={(e) => setAccessCode(e.target.value)} placeholder="Enter access code" />
            )}
          </Card>

          <Card>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Customer Details</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <Input label="First Name *" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="John" />
              <Input label="Last Name *" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" />
            </div>
            <Input label="Phone Number *" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1234567890" />
            <Input label="Quantity" type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </Card>
        </div>

        <div>
          <Card className="sticky top-24">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Summary</h3>
            <div className="space-y-3 mb-5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Wallet Balance</span>
                <span className={`font-bold ${walletBalance >= agentPays ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(walletBalance, currency)}</span>
              </div>
              {selectedRaffle && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Total</span>
                    <span>{formatCurrency(totalCost, selectedRaffle.currency)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Commission (10%)</span>
                    <span className="text-emerald-600">-{formatCurrency(commission, selectedRaffle.currency)}</span>
                  </div>
                  <div className="border-t border-gray-100 dark:border-gray-800 pt-3 flex justify-between font-bold text-lg">
                    <span>You Pay</span>
                    <span className="text-indigo-600">{formatCurrency(agentPays, selectedRaffle.currency)}</span>
                  </div>
                  {walletBalance < agentPays && (
                    <div className="bg-indigo-50 dark:bg-indigo-950/50 border border-indigo-100 dark:border-indigo-800 rounded-xl p-3 mt-2">
                      <p className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">Shortfall of {formatCurrency(agentPays - walletBalance, selectedRaffle.currency)} will be charged via Paystack</p>
                    </div>
                  )}
                </>
              )}
            </div>
            <Button fullWidth onClick={handleSell} isLoading={selling} disabled={!raffleId || !firstName || !lastName || !phone}>
              Sell Tickets
            </Button>
          </Card>
        </div>
      </div>
    </AgentLayout>
  );
};
